import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
class TenantCreationProgress {
    modal = null;
    channel = null;
    tenantId = null;
    redirectUrl = null;
    timeout = null;
    steps = [
        'database_created',
        'database_migrated',
        'database_seeded',
        'settings_initialized',
        'infernal_staff_inserted',
    ];
    completedSteps = new Set();

    constructor() {
        this.init();
    }

    init() {
        this.bindCloseButtons();
    }

    bindCloseButtons() {
        const closeBtn = document.getElementById('closeTenantCreationModal');
        if (closeBtn && !closeBtn.dataset.bound) {
            closeBtn.dataset.bound = 'true';
            closeBtn.addEventListener('click', () => {
                this.close();
            });
        }
        const headerCloseBtn = document.getElementById('headerCloseTenantCreationModal');
        if (headerCloseBtn && !headerCloseBtn.dataset.bound) {
            headerCloseBtn.dataset.bound = 'true';
            headerCloseBtn.addEventListener('click', () => {
                this.close();
            });
        }
    }

    show(tenantId, redirectUrl = null) {
        console.log('Showing tenant creation progress modal for tenant:', tenantId);

        this.tenantId = tenantId;
        this.redirectUrl = redirectUrl;

        // Re-bind close buttons (in case modal was added dynamically)
        this.bindCloseButtons();

        // Reset modal state
        this.resetModal();

        // Show modal
        this.modal = $('#tenantCreationProgressModal').modal({ 'show': true });

        // Fetch existing progress from server (in case we missed WebSocket events)
        this.fetchExistingProgress();

        // Initialize Echo and subscribe to channel
        this.initializeEcho();
        this.subscribeToChannel();

        // Set timeout for completion
        this.timeout = setTimeout(() => {
            this.showError('Tenant creation is taking longer than expected. Please check back later.');
        }, 300000); // 5 minutes
    }

    async fetchExistingProgress() {
        try {
            const response = await fetch(`/organizations/${this.tenantId}/creation-progress`, {
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                }
            });

            if (!response.ok) {
                console.log('No existing progress found');
                return;
            }

            const data = await response.json();
            if (data.success && data.progress) {
                console.log('Fetched existing progress:', data.progress);

                // Mark completed steps
                const completedSteps = data.progress.completed_steps || {};
                for (const [step, info] of Object.entries(completedSteps)) {
                    this.markStepCompleted(step, info.message || '');
                }

                // Check if all steps are completed
                if (data.progress.completed) {
                    this.onCreationComplete();
                }

                // Check for errors
                if (data.progress.error) {
                    this.showError(data.progress.error);
                }
            }
        } catch (error) {
            console.error('Error fetching progress:', error);
        }
    }

    subscribeToChannel() {
        if (!this.tenantId) {
            console.error('Tenant ID not available');
            return;
        }

        // Initialize Echo if not available
        if (!globalThis.Echo) {
            this.initializeEcho();
        }

        if (!globalThis.Echo) {
            console.error('Echo could not be initialized');
            return;
        }

        // Leave any existing channel
        if (this.channel && globalThis.Echo) {
            globalThis.Echo.leave(`tenant-creation.${this.tenantId}`);
        }

        // Subscribe to private channel
        const channelName = `tenant-creation.${this.tenantId}`;
        console.log('Subscribing to channel:', channelName);
        this.channel = globalThis.Echo.private(channelName);

        // Listen for "in progress" events (step started)
        this.channel.listen('.tenant.database.creating', (event) => {
            console.log('Received tenant.database.creating event:', event);
            this.markStepInProgress('database_created', event.message);
        });

        this.channel.listen('.tenant.database.migrating', (event) => {
            console.log('Received tenant.database.migrating event:', event);
            this.markStepInProgress('database_migrated', event.message);
        });

        this.channel.listen('.tenant.database.seeding', (event) => {
            console.log('Received tenant.database.seeding event:', event);
            this.markStepInProgress('database_seeded', event.message);
        });

        this.channel.listen('.tenant.settings.initializing', (event) => {
            console.log('Received tenant.settings.initializing event:', event);
            this.markStepInProgress('settings_initialized', event.message);
        });

        this.channel.listen('.tenant.infernal_staff.inserting', (event) => {
            console.log('Received tenant.infernal_staff.inserting event:', event);
            this.markStepInProgress('infernal_staff_inserted', event.message);
        });

        // Listen for "completed" events
        this.channel.listen('.tenant.database.created', (event) => {
            console.log('Received tenant.database.created event:', event);
            this.markStepCompleted('database_created', event.message);
        });

        this.channel.listen('.tenant.database.migrated', (event) => {
            console.log('Received tenant.database.migrated event:', event);
            this.markStepCompleted('database_migrated', event.message);
        });

        this.channel.listen('.tenant.database.seeded', (event) => {
            console.log('Received tenant.database.seeded event:', event);
            this.markStepCompleted('database_seeded', event.message);
        });

        this.channel.listen('.tenant.settings.initialized', (event) => {
            console.log('Received tenant.settings.initialized event:', event);
            this.markStepCompleted('settings_initialized', event.message);
        });

        this.channel.listen('.tenant.infernal_staff.inserted', (event) => {
            console.log('Received tenant.infernal_staff.inserted event:', event);
            this.markStepCompleted('infernal_staff_inserted', event.message);
            this.onCreationComplete();
        });

        // Handle connection errors
        this.channel.listen('.error', (event) => {
            console.log('Received error event:', event);
            this.markStepError(event.step, event.message);
            this.showError(event.message || 'An error occurred during tenant creation.');
        });

        // Listen for subscription success
        this.channel.subscribed(() => {
            console.log('Successfully subscribed to tenant-creation.' + this.tenantId);
        });
    }

    initializeEcho() {
        // Check if required variables are available
        if (!globalThis.PUSHER_KEY || !globalThis.PUSHER_CLUSTER) {
            console.error('Pusher configuration not available');
            return;
        }

        try {
            // Make Pusher available globally
            globalThis.Pusher = Pusher;

            // Initialize Echo
            globalThis.Echo = new Echo({
                broadcaster: 'pusher',
                key: globalThis.PUSHER_KEY,
                cluster: globalThis.PUSHER_CLUSTER,
                authEndpoint: '/central-broadcasting/auth',
                forceTLS: true,
                auth: {
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                        Accept: 'application/json',
                    }
                }
            });

            console.log('Echo initialized successfully');
        } catch (error) {
            console.error('Error initializing Echo:', error);
        }
    }

    markStepInProgress(step, message = '') {
        if (this.completedSteps.has(step)) {
            return;
        }

        const stepElement = document.querySelector(`[data-step="${step}"]`);
        if (stepElement) {
            stepElement.classList.remove('pending', 'errored');
            stepElement.classList.add('in-progress');

            const statusIcon = stepElement.querySelector('.step-status i');
            if (statusIcon) {
                statusIcon.className = 'fas fa-spinner fa-spin text-primary';
            }

            const messageElement = stepElement.querySelector('.step-content small');
            if (messageElement && message) {
                messageElement.textContent = message;
            }
        }

        console.log(`Step in progress: ${step}`, message);
    }

    markStepCompleted(step, message = '') {
        if (this.completedSteps.has(step)) {
            return;
        }

        this.completedSteps.add(step);

        const stepElement = document.querySelector(`[data-step="${step}"]`);
        if (stepElement) {
            // Mark as completed
            stepElement.classList.remove('pending', 'in-progress', 'errored');
            stepElement.classList.add('completed');

            // Update icon
            const statusIcon = stepElement.querySelector('.step-status i');
            if (statusIcon) {
                statusIcon.className = 'fas fa-check';
            }

            // Update message if provided
            const messageElement = stepElement.querySelector('.step-content small');
            if (messageElement && message) {
                messageElement.textContent = message;
            }
        }

        console.log(`Step completed: ${step}`, message);
    }

    markStepError(step, message = '') {
        if (!step) {
            return;
        }

        const stepElement = document.querySelector(`[data-step="${step}"]`);
        if (stepElement) {
            stepElement.classList.remove('in-progress', 'completed');
            stepElement.classList.add('errored');

            const statusIcon = stepElement.querySelector('.step-status i');
            if (statusIcon) {
                statusIcon.className = 'fas fa-exclamation-triangle text-danger';
            }

            const messageElement = stepElement.querySelector('.step-content small');
            if (messageElement && message) {
                messageElement.textContent = message;
            }
        }

        console.log(`Step errored: ${step}`, message);
    }

    resetSteps() {
        this.steps.forEach(step => {
            const stepElement = document.querySelector(`[data-step="${step}"]`);
            if (stepElement) {
                stepElement.classList.remove('completed', 'in-progress', 'errored');
                stepElement.classList.add('pending');
                const statusIcon = stepElement.querySelector('.step-status i');
                if (statusIcon) {
                    statusIcon.className = 'fas fa-hourglass-half text-muted';
                }
            }
        });
    }

    resetModal() {
        // Clear completed steps
        this.completedSteps.clear();

        // Reset all step UI elements
        this.resetSteps();

        // Show spinner
        const spinner = document.querySelector('.spinner-border');
        if (spinner) {
            spinner.style.display = '';
        }

        // Reset loading text
        const successText = document.querySelector('#successText');
        if (successText) {
            successText.classList.add('d-none');
            const loadingText = document.querySelector('#loadingText');
            if (loadingText) {
                loadingText.classList.remove('d-none');
            }
        }

        // Hide error message
        const errorDiv = document.getElementById('tenantCreationError');
        if (errorDiv) {
            errorDiv.classList.add('d-none');
        }

        // Hide close button
        const closeBtn = document.getElementById('closeTenantCreationModal');
        if (closeBtn) {
            closeBtn.classList.add('d-none');
        }
    }

    onCreationComplete() {
        // Clear timeout
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }

        // All steps completed
        setTimeout(() => {
            this.hideSpinner();
            this.showCloseButton();
        }, 500);
    }

    hideSpinner() {
        const spinner = document.querySelector('.spinner-border');
        if (spinner) {
            spinner.style.display = 'none';
        }

        const loadingText = document.querySelector('#loadingText');
        if (loadingText) {
            loadingText.classList.add('d-none');
            const successText = document.querySelector('#successText');
            if (successText) {
                successText.classList.remove('d-none');
            }
            const successIcon = document.querySelector('#successIcon');
            if (successIcon) {
                successIcon.classList.remove('d-none');
            }
        }
    }

    showCloseButton() {
        const closeBtn = document.getElementById('closeTenantCreationModal');
        if (closeBtn) {
            closeBtn.classList.remove('d-none');
        }
    }

    showError(message) {
        // Clear timeout
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }

        // Hide spinner
        this.hideSpinner();

        // Show error message
        const errorDiv = document.getElementById('tenantCreationError');
        const errorMessage = document.getElementById('tenantCreationErrorMessage');

        if (errorDiv && errorMessage) {
            errorMessage.textContent = message;
            errorDiv.classList.remove('d-none');
        }

        // Show close button
        this.showCloseButton();

        console.error('Tenant creation error:', message);
    }

    hideError() {
        const errorDiv = document.getElementById('tenantCreationError');
        if (errorDiv) {
            errorDiv.classList.add('d-none');
        }
    }

    close() {
        // Leave channel using Echo.leave()
        if (this.tenantId && globalThis.Echo) {
            globalThis.Echo.leave(`tenant-creation.${this.tenantId}`);
        }
        this.channel = null;

        if (this.redirectUrl) {
            globalThis.location.href = this.redirectUrl;
            return;
        }

        globalThis.location.href = '/organizations';
    }
}

// Always initialize and expose globally
// The class handles missing modal gracefully
globalThis.tenantCreationProgress = new TenantCreationProgress();

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
    module.exports = TenantCreationProgress;
}
